import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { EyeIcon, EyeSlashIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { usePortalAuth } from "../context/PortalAuthContext";
import { selectClsLg } from "../constants/formClasses";
import PortalSelect from "../components/PortalSelect";
import { MEXICAN_STATES } from "../data/mexicanStates";
import EmployerAccountSelects from "../components/register/EmployerAccountSelects";

const inputCls =
  "w-full rounded-xl border border-[#d4cceb] bg-white px-4 py-3 text-[0.9375rem] text-brand-dark placeholder:text-brand-gray/50 focus:border-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/15 transition-colors";

const CONSULTATION_REASONS = [
  { value: "estres", label: "Estrés" },
  { value: "ansiedad", label: "Ansiedad" },
  { value: "depresion", label: "Depresión" },
  { value: "duelo_perdida", label: "Duelo / pérdida" },
  { value: "problemas_pareja", label: "Problemas de pareja" },
  { value: "problemas_familia", label: "Problemas de familia" },
  { value: "autoestima_seguridad", label: "Autoestima / seguridad" },
  { value: "crianza_hijos", label: "Crianza / hijos" },
  { value: "trastornos_alimenticios", label: "Trastornos alimenticios" },
  { value: "trastornos_sueno", label: "Trastornos de sueño" },
  { value: "otro", label: "Otro" },
];

const SEX_OPTIONS = [
  { value: "hombre", label: "Hombre" },
  { value: "mujer", label: "Mujer" },
  { value: "no_binario", label: "No binario" },
  { value: "otro", label: "Otro" },
];

export default function PortalRegister() {
  const { register, refreshMe, isAuthenticated, user } = usePortalAuth();
  const navigate = useNavigate();

  const [userType, setUserType] = useState(""); // "employee" | "beneficiary"
  const [form, setForm] = useState({
    companyEmployerId: "",
    companyAccountId: "",
    state: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "",
    sex: "",
    position: "",
    seniority: "",
    consultationReason: "",
    password: "",
    passwordConfirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated && user?.role === "client") navigate("/panel", { replace: true });
  }, [isAuthenticated, user, navigate]);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setCompanySelection(sel) {
    setForm((f) => ({
      ...f,
      companyEmployerId: sel.employerId,
      companyAccountId: sel.accountId,
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!userType) {
      toast.error("Selecciona si eres empleado o beneficiario");
      return;
    }
    if (form.password !== form.passwordConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (form.password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (userType === "employee" && (!form.companyEmployerId || !form.companyAccountId)) {
      toast.error("Selecciona tu empresa y subempresa o cuenta");
      return;
    }
    setLoading(true);
    try {
      await register({
        userType,
        ...(userType === "employee" && {
          companyEmployerId: form.companyEmployerId,
          companyAccountId: form.companyAccountId,
          state: form.state,
          position: form.position,
          seniority: form.seniority,
        }),
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        age: form.age,
        sex: form.sex,
        consultationReason: form.consultationReason,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
      });
      await refreshMe();
      toast.success("Cuenta creada correctamente");
      navigate("/panel", { replace: true });
    } catch (err) {
      toast.error(err.message || "No se pudo registrar");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* ── Left — form ── */}
      <div className="flex w-full flex-col items-center bg-white lg:w-1/2 min-h-screen">
        <div className="w-full max-w-md px-8 py-16 my-auto">

          {/* Header */}
          <div className="mb-6">
            <h1 className="font-sans text-[2rem] font-semibold tracking-tight text-brand-dark">
              Crear cuenta
            </h1>
            <p className="mt-2 text-[0.9375rem] text-brand-gray leading-snug">
              Servicios disponibles para colaboradores con contrato PAE.
            </p>
          </div>

          {/* IMPORTANTE banner */}
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm leading-relaxed">
              <span className="font-semibold text-red-700">IMPORTANTE: </span>
              <span className="text-red-700">
                En caso de que requieras asistencia inmediata, comunícate a los teléfonos{" "}
                <a href="tel:8006819591" className="font-bold underline">800 681 9591</a>
                {" "}y{" "}
                <a href="tel:3341708343" className="font-bold underline">33 4170 8343</a>
                , disponibles las 24 horas, los 365 días del año.
                Atención profesional · especializada · confidencial · gratuita.
              </span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>

            {/* ── User type toggle ── */}
            <div className="space-y-1.5">
              <label className="portal-label">Soy</label>
              <div className="grid grid-cols-2 gap-3" role="radiogroup">
                {[
                  { value: "employee", label: "Empleado" },
                  { value: "beneficiary", label: "Beneficiario" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${
                      userType === opt.value
                        ? "border-brand-navy bg-brand-navy/5 text-brand-navy"
                        : "border-[#d4cceb] text-brand-gray hover:border-brand-lavender/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="userType"
                      value={opt.value}
                      checked={userType === opt.value}
                      onChange={() => setUserType(opt.value)}
                      className="sr-only"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* ── Employee fields ── */}
            {userType === "employee" && (
              <>
                <EmployerAccountSelects
                  value={{ employerId: form.companyEmployerId, accountId: form.companyAccountId }}
                  onChange={setCompanySelection}
                  disabled={loading}
                />
                <div className="space-y-1.5">
                  <label className="portal-label">Estado (México)</label>
                  <PortalSelect
                    size="lg"
                    className={selectClsLg}
                    value={form.state}
                    onChange={(e) => setField("state", e.target.value)}
                    required
                  >
                    <option value="">Selecciona tu estado</option>
                    {MEXICAN_STATES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </PortalSelect>
                </div>
              </>
            )}

            {/* ── Common fields (shown once type is selected) ── */}
            {userType && (
              <>
                {/* Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="firstName" className="portal-label">Nombre(s)</label>
                    <input
                      id="firstName"
                      type="text"
                      value={form.firstName}
                      onChange={(e) => setField("firstName", e.target.value)}
                      required
                      autoComplete="given-name"
                      placeholder="Ana"
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="lastName" className="portal-label">Apellidos</label>
                    <input
                      id="lastName"
                      type="text"
                      value={form.lastName}
                      onChange={(e) => setField("lastName", e.target.value)}
                      required
                      autoComplete="family-name"
                      placeholder="García"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Age + Sex */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="age" className="portal-label">Edad</label>
                    <input
                      id="age"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={form.age}
                      onChange={(e) => setField("age", e.target.value.replace(/\D/g, ""))}
                      placeholder="30"
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="sex" className="portal-label">Sexo</label>
                    <PortalSelect
                      size="lg"
                      className={selectClsLg}
                      value={form.sex}
                      onChange={(e) => setField("sex", e.target.value)}
                    >
                      <option value="">Selecciona</option>
                      {SEX_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </PortalSelect>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="portal-label">Correo electrónico</label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="tu@correo.com"
                    className={inputCls}
                  />
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label htmlFor="phone" className="portal-label">Teléfono</label>
                  <input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    required
                    autoComplete="tel"
                    placeholder="55 1234 5678"
                    className={inputCls}
                  />
                </div>

                {/* Employee-only: Puesto + Antigüedad */}
                {userType === "employee" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label htmlFor="position" className="portal-label">Puesto</label>
                      <input
                        id="position"
                        type="text"
                        value={form.position}
                        onChange={(e) => setField("position", e.target.value)}
                        placeholder="Analista"
                        className={inputCls}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="seniority" className="portal-label">
                        Antigüedad{" "}
                        <span className="font-normal text-brand-gray">(años)</span>
                      </label>
                      <input
                        id="seniority"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={form.seniority}
                        onChange={(e) => setField("seniority", e.target.value.replace(/\D/g, ""))}
                        placeholder="3"
                        className={inputCls}
                      />
                    </div>
                  </div>
                )}

                {/* Motivo de consulta */}
                <div className="space-y-1.5">
                  <label htmlFor="consultationReason" className="portal-label">
                    Motivo de consulta
                  </label>
                  <PortalSelect
                    size="lg"
                    className={selectClsLg}
                    value={form.consultationReason}
                    onChange={(e) => setField("consultationReason", e.target.value)}
                  >
                    <option value="">Selecciona un motivo</option>
                    {CONSULTATION_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </PortalSelect>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="password" className="portal-label">
                    Contraseña{" "}
                    <span className="font-normal text-brand-gray">(mín. 8 caracteres)</span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className={`${inputCls} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-brand-gray hover:text-brand-dark transition-colors"
                      aria-label={showPassword ? "Ocultar" : "Mostrar"}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div className="space-y-1.5">
                  <label htmlFor="passwordConfirm" className="portal-label">
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <input
                      id="passwordConfirm"
                      type={showConfirm ? "text" : "password"}
                      value={form.passwordConfirm}
                      onChange={(e) => setField("passwordConfirm", e.target.value)}
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className={`${inputCls} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-brand-gray hover:text-brand-dark transition-colors"
                      aria-label={showConfirm ? "Ocultar" : "Mostrar"}
                    >
                      {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-brand-navy py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-navy/20 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? "Creando cuenta…" : "Registrarme"}
                </button>
              </>
            )}
          </form>

          <p className="mt-8 text-center text-[0.9375rem] text-brand-gray">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="font-semibold text-brand-navy hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>

      {/* ── Right — brand panel (fixed to viewport) ── */}
      <div
        className="hidden lg:flex flex-col items-center justify-center bg-brand-navy px-12 py-16 fixed right-0 top-0 w-1/2 h-screen"
        aria-hidden
      >
        <img
          src="/images/Logo-v2.svg"
          alt=""
          className="w-full max-w-[26rem] brightness-0 invert"
          draggable={false}
        />
        <p className="mt-8 text-[0.9375rem] font-light tracking-widest text-white/60 uppercase">
          Programa de Asistencia al Empleado
        </p>
      </div>
    </div>
  );
}
